// RequestsSection.tsx - Simplified version without drawer
import React, {
  useState,
  useCallback,
  useMemo,
  Suspense,
  createContext,
  useEffect,
  useImperativeHandle,
} from "react";
import { Modal, message, Card } from "antd";
import { useUserPanel } from "../../contexts/UserPanelContext";
import { CancelWithdrawModal } from "../CancelWithdrawModal";
import { CreateLoadRequestModal } from "../../components/CreateLoadRequestModal";
import { UnifiedRequestsTable } from "../RequestsPanel/UnifiedRequestsTable";
import styled from "styled-components";
import axios from "axios";
import { API_BASE_URL } from "../../constants/config";
import { useMutation } from "@tanstack/react-query";
import {
  RequestData,
} from "../RequestsPanel/types";
import { useTranslation } from "react-i18next";
import { useRequestsStore } from "../../shared/stores/useRequestsStore";
import { getAndSanitize } from "../../shared/utils/sanitization";
import { useEffectiveAdminId } from "../../contexts/AdminSelectorContext";
import { useOutletContext } from "react-router-dom";

export interface RequestsSectionContextState {
  modalState: {
    imageModalVisible: boolean;
    setImageModalVisible: (value: boolean) => void;
    selectedImage: string | null;
    setSelectedImage: (value: string | null) => void;
    cancelWithdrawModalOpen: {
      open: boolean;
      request: RequestData | null;
    };
    setCancelWithdrawModalOpen: (value: any) => void;
    createLoadRequestModalOpen: boolean;
    setCreateLoadRequestModalOpen: (value: boolean) => void;
  };
  processingState?: {
    processingRequestIds: string[];
    isProcessing: (requestId: string) => boolean;
  };
}

const StyledCard = styled(Card)`
  height: 100%;

  .ant-card-body {
    height: 100%;
    padding: 16px 24px;
    overflow: auto;
  }
`;

const RequestsSectionContext = createContext<RequestsSectionContextState | null>(
  null
);

export interface RequestsSectionRef {
  openCreateLoadRequestModal: () => void;
}

export const RequestsSection: React.FC = () => {
  const { currentPanelId } = useUserPanel();
  const {
    fetchRequests
  } = useRequestsStore();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [imageModalVisible, setImageModalVisible] = useState<boolean>(false);
  const [createLoadRequestModalOpen, setCreateLoadRequestModalOpen] = useState<boolean>(false);
  const { t } = useTranslation();
  const [processingRequestIds, setProcessingRequestIds] = useState<string[]>([]);

  // Get effective admin ID for filtering (respects SuperAdmin admin selector)
  const effectiveAdminId = useEffectiveAdminId();

  // Get ref from outlet context and expose function to open modal
  const context = useOutletContext<{ requestsSectionRef?: React.RefObject<RequestsSectionRef> }>();
  const contextRef = context?.requestsSectionRef;

  const isProcessing = useCallback((requestId: string) => {
    return processingRequestIds.includes(requestId);
  }, [processingRequestIds]);

  // Expose function to open create load request modal through context ref
  useImperativeHandle(contextRef, () => ({
    openCreateLoadRequestModal: () => {
      setCreateLoadRequestModalOpen(true);
    },
  }), []);

  const [cancelWithdrawModalOpen, setCancelWithdrawModalOpen] = useState<{
    open: boolean;
    request: RequestData | null;
  }>({
    open: false,
    request: null,
  });

  const cancelWithdrawMutation = useMutation({
    mutationFn: async ({
      requestId,
      rejectionReason,
      panelId,
    }: {
      requestId: string;
      rejectionReason: string;
      panelId: string;
    }) => {
      setProcessingRequestIds(prev => [...prev, requestId]);

      const response = await axios.post(
        `${API_BASE_URL}/api/withdraw-requests/withdraw-requests/cancel`,
        {
          requestId,
          rejectionReason,
          panelId,
        },
        {
          headers: { Authorization: `Bearer ${getAndSanitize("token")}` },
        }
      );
      return response.data;
    },
    onSuccess: async (_, variables) => {
      // ✅ FIX: Confiar en websocket + store debounced refetch
      // El delay da tiempo a que el websocket llegue y actualice el store correctamente
      await new Promise(resolve => setTimeout(resolve, 600));

      message.success(
        t("requestsPanel.notifications.success.withdrawCanceled")
      );
      setCancelWithdrawModalOpen({ open: false, request: null });
      setProcessingRequestIds(prev => prev.filter(id => id !== variables.requestId));
    },
    onError: (error: any, variables) => {
      console.error("[CancelWithdraw] Error:", error);
      message.error(
        error.response?.data?.message ||
          t("requestsPanel.notifications.error.withdrawCancelError")
      );
      setProcessingRequestIds(prev => prev.filter(id => id !== variables.requestId));
    },
  });

  const handleCancelWithdraw = useCallback(
    async (requestId: string, reason: string) => {
      if (!requestId || !reason || !currentPanelId) return;

      await cancelWithdrawMutation.mutateAsync({
        requestId,
        rejectionReason: reason,
        panelId: currentPanelId,
      });
    },
    [cancelWithdrawMutation, currentPanelId]
  );

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        await Promise.all([
          fetchRequests("pending", 1, 10, undefined, false, effectiveAdminId),
          fetchRequests("approvedWithdraws", 1, 10, undefined, false, effectiveAdminId),
          fetchRequests("completedRejected", 1, 10, undefined, false, effectiveAdminId)
        ]);
      } catch (error) {
        console.error("Error fetching initial data:", error);
        message.error(t("requestsPanel.notifications.error.loadFailed"));
      }
    };

    fetchInitialData();
  }, [effectiveAdminId]);

  const contextValue: RequestsSectionContextState = useMemo(
    () => ({
      modalState: {
        imageModalVisible,
        setImageModalVisible,
        selectedImage,
        setSelectedImage,
        cancelWithdrawModalOpen,
        setCancelWithdrawModalOpen,
        createLoadRequestModalOpen,
        setCreateLoadRequestModalOpen,
      },
      processingState: {
        processingRequestIds,
        isProcessing
      }
    }),
    [
      imageModalVisible,
      selectedImage,
      cancelWithdrawModalOpen,
      createLoadRequestModalOpen,
      processingRequestIds,
      isProcessing
    ]
  );

  function hasRequestsGetPermission(): boolean {
    try {
      const userPermissions = JSON.parse(getAndSanitize("user_permissions") || "{}");
      const userRole = getAndSanitize("role");
      return userPermissions["/api/requests:GET"] === true || userRole !== "USER";
    } catch (error) {
      console.error("[Permissions] Failed to check requests GET permission:", error);
      return false;
    }
  }

  if (!hasRequestsGetPermission()) {
    return null;
  }

  return (
    <RequestsSectionContext.Provider value={contextValue}>
      <StyledCard>
        <Suspense fallback={<div>Loading...</div>}>
          <UnifiedRequestsTable
            onCancelWithdraw={(request) => setCancelWithdrawModalOpen({ open: true, request })}
            processingRequestIds={processingRequestIds}
          />
        </Suspense>
      </StyledCard>

      <CancelWithdrawModal
        open={cancelWithdrawModalOpen.open}
        request={
          cancelWithdrawModalOpen.request
            ? {
                ...cancelWithdrawModalOpen.request,
                processingSteps: cancelWithdrawModalOpen.request
                  .processingSteps as any,
                retryHistory:
                  cancelWithdrawModalOpen.request.retryHistory || [],
              }
            : null
        }
        onCancel={() =>
          setCancelWithdrawModalOpen({ open: false, request: null })
        }
        onConfirm={handleCancelWithdraw}
        isLoading={cancelWithdrawMutation.isPending}
      />

      <CreateLoadRequestModal
        open={createLoadRequestModalOpen}
        onCancel={() => setCreateLoadRequestModalOpen(false)}
        onSuccess={async () => {
          // ✅ FIX: Confiar en websocket + store debounced refetch
          // 600ms da tiempo a websocket (50-100ms) + store debounce (500ms) = actualización completa
          await new Promise(resolve => setTimeout(resolve, 600));

          message.success('Load request created and validation started');
        }}
      />

      <Modal
        open={imageModalVisible}
        footer={null}
        onCancel={() => {
          setImageModalVisible(false);
          setSelectedImage(null);
        }}
      >
        {selectedImage && (
          <img
            src={selectedImage}
            style={{ width: "100%", height: "auto" }}
            alt="Receipt"
          />
        )}
      </Modal>
    </RequestsSectionContext.Provider>
  );
};

export default RequestsSection;